import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/auth.service';
import { getMyScreenPermissions } from '../services/userManagementService';
import { LoginResponse } from '../types';
import { usePermissions } from './PermissionsContext';

interface UserInfo {
  userId: number | null;
  companyId: number | null;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  user: UserInfo;
}

// Decode JWT token payload without external library
const decodeToken = (token: string): { sub?: string; aud?: string } | null => {
  try {
    const base64Payload = token.split('.')[1];
    const payload = atob(base64Payload);
    return JSON.parse(payload);
  } catch {
    return null;
  }
};

// Extract user info from JWT token
const getUserFromToken = (token: string | null): UserInfo => {
  if (!token) return { userId: null, companyId: null };
  
  const decoded = decodeToken(token);
  if (!decoded) return { userId: null, companyId: null };
  
  return {
    companyId: decoded.sub ? parseInt(decoded.sub, 10) : null,
    userId: decoded.aud ? parseInt(decoded.aud, 10) : null,
  };
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { setPermissions, clearPermissions } = usePermissions();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<UserInfo>({ userId: null, companyId: null });

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = authService.getToken();
      setIsAuthenticated(!!token);
      setUser(getUserFromToken(token));
      
      // If user is already authenticated, fetch permissions
      if (token) {
        try {
          const permissionsResponse = await getMyScreenPermissions();
          setPermissions(permissionsResponse.permissions);
        } catch (err) {
          console.error('Failed to fetch permissions on mount:', err);
        }
      }
      
      setIsLoading(false);
    };

    checkAuth();
  }, [setPermissions]);

  // Refresh company restrictions after platform changes and tenant switching.
  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      getMyScreenPermissions().then(response => {
        if (active) setPermissions(response.permissions);
      }).catch(() => { if (active) clearPermissions(); });
    };
    const timer = window.setInterval(refresh, 60000);
    window.addEventListener('focus', refresh);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [isAuthenticated, setPermissions, clearPermissions]);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response: LoginResponse = await authService.login({ email, password });
      
      if (response.access_token) {
        const token = authService.getToken();
        setUser(getUserFromToken(token));
        
        // Fetch user permissions after successful login
        try {
          const permissionsResponse = await getMyScreenPermissions();
          setPermissions(permissionsResponse.permissions);
        } catch (permErr) {
          console.error('Failed to fetch permissions:', permErr);
          // Continue with login even if permissions fetch fails
        }
        
        setIsAuthenticated(true); // Set this last to trigger dependent effects
      } else {
        throw new Error('Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.logout();
      setIsAuthenticated(false);
      setUser({ userId: null, companyId: null });
      clearPermissions(); // Clear permissions on logout
      setError(null);
    } catch (err) {
      console.error('Logout error:', err);
      // Clear auth state even if logout API fails
      setIsAuthenticated(false);
      setUser({ userId: null, companyId: null });
      clearPermissions();
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    login,
    logout,
    error,
    user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
